/**
 * Sorts a recruiting export into the two systems that own its parts.
 *
 * One export holds seven files per job:
 *
 *   NN_Name.html                          → CMS, the campaign page
 *   NN_Name_GoogleJobs.json               → CMS, the structured data
 *   NN_Name_Fragebogen2.html              → PocketBase, the assessment
 *   NN_Name_Email_Einladung_Fragebogen2.md → PocketBase, its invitation mail
 *   NN_Name_Indeed.txt / _AfA.txt         → PocketBase, the ad text per channel
 *   NN_Name_Storyboard.md                 → PocketBase, kept for the download
 *
 * The CMS maps the JSON onto its fields and drops the file, so PocketBase keeps
 * a copy of it too. That is what lets the dashboard hand the export back.
 *
 * The handover also ships NN_Name_INDEED.md, HR's field-by-field package for the
 * Indeed form. It shares the base name with the ad text, so the channel kinds
 * match .txt only; otherwise whichever file came later in the ZIP would win.
 *
 * Splitting here rather than server-side is what lets one upload reach two
 * systems: the CMS cannot write to PocketBase, and should not be able to.
 */

/** Suffixes that identify a companion, longest first so the specific ones win. */
const KINDS = [
  { key: 'invitation', test: /_Email_Einladung[^.]*\.(md|markdown|txt)$/i },
  { key: 'assessment', test: /_Fragebogen\d+\.html?$/i },
  { key: 'json', test: /_GoogleJobs\.json$/i },
  { key: 'indeed', test: /_Indeed\.txt$/i },
  { key: 'afa', test: /_AfA\.txt$/i },
  { key: 'storyboard', test: /_Storyboard\.md$/i },
  { key: 'ignored', test: /_(AfA|Indeed|Storyboard)\.[^.]+$/i },
];

/** Documents about a person, never about a job. */
const PERSONAL = /_(Auswertung|Gespraechsnotiz|Gesprächsnotiz|Bewertung)_/i;

export const PERSONAL_REASON =
  'Bewerber-Dokument (Auswertung, Gesprächsnotiz). Wird nicht hochgeladen.';

/** Strip the companion suffix and the extension to get the job's base name. */
export function jobBaseName(filename) {
  return filename
    .replace(/\.(html?|json|md|markdown|txt)$/i, '')
    .replace(/_Email_Einladung.*$/i, '')
    .replace(/_Fragebogen\d+$/i, '')
    .replace(/_GoogleJobs$/i, '')
    .replace(/_(AfA|Indeed|Storyboard)$/i, '');
}

function classify(filename) {
  for (const kind of KINDS) {
    if (kind.test.test(filename)) return kind.key;
  }
  return /\.html?$/i.test(filename) ? 'campaign' : 'other';
}

/**
 * Group the entries of an unpacked ZIP by job.
 *
 * `entries` is a { path: Uint8Array } map as fflate's unzipSync returns it.
 * Returns one row per job plus a list of what was left out and why, so the
 * preview can say it rather than the upload quietly dropping things.
 */
export function groupZipEntries(entries) {
  const jobs = new Map();
  const skipped = [];
  const seenPersonal = new Set();

  for (const [path, bytes] of Object.entries(entries)) {
    if (path.endsWith('/')) continue;
    if (path.includes('__MACOSX/')) continue;

    const filename = path.split('/').pop() || path;
    if (filename.startsWith('.')) continue;

    if (PERSONAL.test(filename)) {
      const base = jobBaseName(filename);
      if (!seenPersonal.has(base)) {
        seenPersonal.add(base);
        skipped.push({ name: base, reason: PERSONAL_REASON });
      }
      continue;
    }

    const kind = classify(filename);
    if (kind === 'ignored' || kind === 'other') continue;

    const base = jobBaseName(filename);
    const job = jobs.get(base) || { baseName: base };
    job[kind] = { filename, bytes };
    jobs.set(base, job);
  }

  const rows = [];
  for (const job of jobs.values()) {
    // A campaign page without its JSON cannot become a job listing, and an
    // assessment without a job has nothing to attach to.
    if (!job.campaign || !job.json) {
      skipped.push({
        name: job.baseName,
        reason: !job.campaign
          ? 'Keine Kampagnenseite (HTML) zu dieser JSON gefunden.'
          : 'Keine GoogleJobs-JSON zu dieser Kampagnenseite gefunden.',
      });
      continue;
    }
    rows.push(job);
  }

  rows.sort((a, b) => a.baseName.localeCompare(b.baseName, 'de'));
  return { jobs: rows, skipped };
}

/** Turn an entry back into a File, which is what both upload paths take. */
export function entryToFile(entry, mime) {
  return new File([entry.bytes], entry.filename, { type: mime });
}
